import { useState, useEffect, useContext, useRef } from "react";
import { useSocket } from "../contexts/SocketContext";
import { AuthContext } from "../contexts/AuthContext";
import axios from "axios";
import { Helmet } from 'react-helmet-async';
import axiosInstance from "../utils/axiosInstance";

const Chat = ({ receiverId }) => {
    const socketRef = useSocket(); // this is the ref
    const socket = socketRef?.current;
    const { userId, token } = useContext(AuthContext);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const bottomRef = useRef(null);
    const [isTyping, setIsTyping] = useState(false);
    const [typingTimeout, setTypingTimeout] = useState(null);

    useEffect(() => {
        // Fetch message history
        const fetchMessages = async () => {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE}/api/messages/${userId}/${receiverId}`,
                { headers: { Authorization: token } }
            );
            setMessages(res.data);
        };

        fetchMessages();

        // Real-time listener
        if (socket) {
            socket.on("receive_message", (data) => {
                console.log("📥 Socket received message:", data);

                const senderId =
                    typeof data.sender === "object" ? data.sender._id : data.sender;

                if (senderId === receiverId) {
                    setMessages((prev) => [...prev, data]);
                }
            });
        }

        if (socket) {
            socket.on("typing", (data) => {
                const fromId = typeof data.fromUserId === "object" ? data.fromUserId._id : data.fromUserId;
                if (fromId === receiverId) {
                    setIsTyping(true);
                    if (typingTimeout) clearTimeout(typingTimeout);
                    setTypingTimeout(setTimeout(() => setIsTyping(false), 3000));
                }
            });
        }

        return () => {
            socket?.off("receive_message");
            clearTimeout(typingTimeout);
        };
    }, [receiverId, userId, token, socket]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const sendMessage = async () => {
        if (!text.trim() || !socket) return;

        const messagePayload = {
            senderId: userId,
            receiverId,
            text
        };

        try {
            const response = await axiosInstance.post(
                `${process.env.REACT_APP_API_BASE}/api/messages`,
                messagePayload,
                {
                    headers: { Authorization: token }
                }
            );

            console.log("Sender ID:", messagePayload.senderId);
            console.log("Receiver ID:", messagePayload.receiverId);

            const savedMessage = response.data;

            console.log("📤 Saved message:", savedMessage);

            socket.emit("send_message", savedMessage);

            setMessages((prev) => [...prev, savedMessage]);
            setText("");
        } catch (err) {
            console.error("❌ Failed to send message:", err);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <title>Chat</title>
                <meta name="description" content="Chat with perfect language partner on PairLingo." />
                <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat" />
            </Helmet>

            <div className="flex-1 overflow-y-auto px-2 mb-2">
                {messages.map((msg, i) => {
                    const senderId =
                        typeof msg.sender === "object" ? msg.sender._id : msg.sender;
                    const isOwnMessage = senderId === userId;

                    return (
                        <div key={i} className={`flex flex-col ${isOwnMessage ? "items-end" : "items-start"} mb-3`}>
                            <div
                                className={`px-4 py-2 rounded-2xl text-sm shadow-md max-w-[70%] whitespace-pre-line ${isOwnMessage
                                    ? "bg-indigo-100 text-gray-900"
                                    : "bg-gray-100 text-gray-800"
                                    }`}
                            >
                                {msg.text}
                            </div>
                            <span className="text-xs text-gray-400 mt-1">
                                {msg.createdAt
                                    ? new Date(msg.createdAt).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })
                                    : "Sending..."}
                            </span>
                        </div>
                    );
                })}

                {/* ✅ Typing Indicator */}
                <div className="min-h-[2rem]">
                    {isTyping && (
                        <div className="text-sm text-gray-500 italic mb-2 text-left">
                            typing...
                        </div>
                    )}
                </div>

                {/* 🔽 Scroll anchor */}
                <div ref={bottomRef} />
            </div>

            <div className="sticky bottom-0 bg-white border-t px-3 py-2 flex items-end gap-2">
                <textarea
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        if (socket && receiverId) {
                            socket.emit("typing", { toUserId: receiverId });
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    rows={1}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    placeholder="Type your message…"
                />
                <button
                    onClick={sendMessage}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm transition"
                >
                    Send
                </button>
            </div>
        </div>
    );
};

export default Chat;